import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Check, Clock, Truck, Users, MapPin, Calculator, ChefHat, Dumbbell, Phone, Instagram, LogIn, Calendar, X } from 'lucide-react';
import PriceCalculatorModal from '../../components/public/PriceCalculatorModal';
import PlanPricingModal from '../../components/public/PlanPricingModal';
import { http } from '../../lib/http';

const LandingPage = ({ onNavigate, showCalculator, setShowCalculator }) => {
  const navigate = useNavigate();
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [weeklyMenu, setWeeklyMenu] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [menuLoading, setMenuLoading] = useState(false);

  const openMenuModal = async () => {
    setShowMenuModal(true);
    setMenuLoading(true);
    try {
      const res = await http.get('/public/weekly-menu');
      if (res.data?.success && res.data.menu?.length > 0) {
        setWeeklyMenu(res.data.menu);
        const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });
        const todayMenu = res.data.menu.find(d => d.day === todayName);
        setSelectedDay(todayMenu || res.data.menu[0]);
      }
    } catch (err) {
      console.error('Failed to load menu:', err);
    } finally {
      setMenuLoading(false);
    }
  };

  // Sync with parent state if provided
  useEffect(() => {
    if (showCalculator !== undefined) {
      setIsCalculatorOpen(showCalculator);
    }
  }, [showCalculator]);

  const handleCloseCalculator = () => {
    setIsCalculatorOpen(false);
    if (setShowCalculator) setShowCalculator(false);
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Top Navigation Bar */}
      <nav className="bg-black text-white py-4 px-4 sm:px-6 sticky top-0 z-50 shadow-2xl border-b-2 border-orange-500">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white hover:text-orange-500 transition-colors">Just Protein</h1>
            <span className="text-orange-500 text-sm font-semibold hidden sm:inline">Gym Meals Service</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white flex items-center gap-2 font-semibold"
              onClick={openMenuModal}
            >
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">This Week's Menu</span>
            </Button>
            <Button
              className="bg-orange-500 text-white hover:bg-orange-600 flex items-center gap-2 font-semibold shadow-lg"
              onClick={() => navigate('/login')}
            >
              <LogIn className="w-4 h-4" />
              Login
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-black via-gray-900 to-black py-12 md:py-20 overflow-hidden">
        {/* Hero Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1667499745120-f9bcef8f584e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtZWFsJTIwcHJlcCUyMGNvbnRhaW5lcnN8ZW58MHx8fHwxNzYxNDQxODg4fDA&ixlib=rb-4.1.0&q=85"
            alt="Healthy Meal Prep"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
          {/* Logo and Title Container */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-8">
            <img
              src="/images/just-protein-logo.png"
              alt="Just Protein Logo"
              className="h-24 w-24 sm:h-40 sm:w-40 md:h-48 md:w-48 object-contain hover:scale-110 transition-all duration-300"
            />
            <div className="text-center sm:text-left">
              <h1 className="text-4xl md:text-6xl font-bold text-white hover:text-orange-500 transition-colors">
                Just Protein
              </h1>
              <p className="text-xl md:text-2xl text-orange-500 font-semibold">Gym Meals Service Provider</p>
            </div>
          </div>

          <p className="text-2xl md:text-3xl mb-4 text-white font-bold">
            Fresh, high-protein meals delivered <span className="text-orange-500">Mon–Fri</span> across the GTA
          </p>
          <p className="text-lg md:text-xl mb-12 text-gray-300 max-w-3xl mx-auto">
            Transform your body with premium meal prep designed for athletes, gym-goers, and health-conscious individuals
          </p>

          {/* Primary CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap mb-6">
            <Button
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-4 text-base sm:px-10 sm:py-7 sm:text-xl font-bold shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 flex items-center gap-3 w-full sm:w-auto justify-center"
              onClick={() => navigate('/signup/meal-plans')}
            >
              Let's get started
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white px-6 py-4 text-base sm:px-10 sm:py-7 sm:text-xl font-bold shadow-2xl transition-all duration-300 hover:scale-105 flex items-center gap-3 w-full sm:w-auto justify-center"
              onClick={() => setIsPricingOpen(true)}
            >
              View Plan & Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Why Just Protein Section */}
      <section id="why" className="py-12 md:py-20 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white">Why <span className="text-orange-500">Just Protein?</span></h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              More than meal prep — we're your partner in transformation
            </p>
          </div>

          <div className="max-w-7xl mx-auto space-y-12">
            {/* Feature 1 - Redesigned Lifestyle Shifter Section */}
            <div className="relative">
              <div className="container mx-auto px-4 sm:px-6 relative z-10">
                {/* Header */}
                <div className="text-center mb-8 md:mb-12">
                  <div className="inline-flex items-center gap-2 sm:gap-3 bg-orange-500 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-full mb-6 shadow-lg">
                    <svg className="h-6 w-6 sm:h-8 sm:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    <span className="font-bold text-base sm:text-lg">Premium Dashboard Access</span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white">
                    Not Just a Meal Prep — <span className="text-orange-500">A Lifestyle Shifter</span>
                  </h3>
                  <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                    We're more than just meal prep — we're your partner in transformation. When you enroll with Just Protein, you gain access to a <span className="text-orange-500 font-bold">personalized Customer Dashboard</span> that helps you track your journey with:
                  </p>
                </div>

                {/* Feature Cards Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                  {/* Card 1 - Orange Theme */}
                  <div className="group relative bg-gradient-to-br from-orange-600 to-orange-700 p-6 rounded-2xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-orange-400/30">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all"></div>
                    <div className="relative z-10">
                      <svg className="h-12 w-12 text-white mb-4 transform group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h4 className="font-bold text-xl mb-2 text-white">Daily Reflections</h4>
                      <p className="text-orange-100 text-sm">Stay mindful and track your progress every single day</p>
                    </div>
                  </div>

                  {/* Card 2 - Orange Theme */}
                  <div className="group relative bg-gradient-to-br from-orange-600 to-orange-700 p-6 rounded-2xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-orange-400/30">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all"></div>
                    <div className="relative z-10">
                      <svg className="h-12 w-12 text-white mb-4 transform group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <h4 className="font-bold text-xl mb-2 text-white">Gym Routine Planning</h4>
                      <p className="text-orange-100 text-sm">Stay consistent with your workout schedule</p>
                    </div>
                  </div>

                  {/* Card 3 - Orange Theme */}
                  <div className="group relative bg-gradient-to-br from-orange-600 to-orange-700 p-6 rounded-2xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-orange-400/30">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all"></div>
                    <div className="relative z-10">
                      <svg className="h-12 w-12 text-white mb-4 transform group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <h4 className="font-bold text-xl mb-2 text-white">Body Measurements</h4>
                      <p className="text-orange-100 text-sm">Track your transformation journey with precision</p>
                    </div>
                  </div>

                  {/* Card 4 - Orange Theme */}
                  <div className="group relative bg-gradient-to-br from-orange-600 to-orange-700 p-6 rounded-2xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-orange-400/30">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all"></div>
                    <div className="relative z-10">
                      <svg className="h-12 w-12 text-white mb-4 transform group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <h4 className="font-bold text-xl mb-2 text-white">Mood Journaling</h4>
                      <p className="text-orange-100 text-sm">Elevate your mindset and mental wellness</p>
                    </div>
                  </div>
                </div>

                {/* Bottom CTA */}
                <div className="text-center p-8 bg-gradient-to-r from-orange-500/20 via-orange-600/20 to-orange-500/20 rounded-2xl border-2 border-orange-500/50 backdrop-blur-sm">
                  <p className="text-2xl md:text-3xl font-bold text-white italic mb-2">
                    Your meals fuel your body
                  </p>
                  <p className="text-xl md:text-2xl font-bold text-orange-500 italic">
                    Your dashboard fuels your mindset
                  </p>
                </div>
              </div>
            </div>

            {/* Features 2, 3, 4 - Premium Three Column Grid */}
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-gray-900 to-black p-5 sm:p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-orange-500 hover:transform hover:scale-105 group">
                <svg className="h-16 w-16 text-orange-500 mb-6 mx-auto group-hover:scale-110 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-2xl font-bold mb-4 text-white text-center">High-Protein, Multi-Cuisine Meals</h3>
                <div className="h-1 w-16 bg-orange-500 mx-auto mb-4 group-hover:w-full transition-all duration-500"></div>
                <p className="text-gray-300 text-center leading-relaxed">
                  Every bite is crafted with your transformation in mind. <span className="font-semibold text-orange-500">Global flavors</span> and <span className="font-semibold text-orange-500">nutrient-dense recipes</span> to help you build muscle and stay energized.
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black p-5 sm:p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-orange-500 hover:transform hover:scale-105 group">
                <svg className="h-16 w-16 text-orange-500 mb-6 mx-auto group-hover:scale-110 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-2xl font-bold mb-4 text-white text-center">Join Our Community</h3>
                <div className="h-1 w-16 bg-orange-500 mx-auto mb-4 group-hover:w-full transition-all duration-500"></div>
                <p className="text-gray-300 text-center leading-relaxed">
                  A tribe of doers who value <span className="font-semibold text-orange-500">fitness, growth, and discipline</span>. Become part of a community that celebrates consistency and results.
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black p-5 sm:p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-orange-500 hover:transform hover:scale-105 group">
                <svg className="h-16 w-16 text-orange-500 mb-6 mx-auto group-hover:scale-110 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <h3 className="text-2xl font-bold mb-4 text-white text-center">Taste. Hygiene. Freshness.</h3>
                <div className="h-1 w-16 bg-orange-500 mx-auto mb-4 group-hover:w-full transition-all duration-500"></div>
                <p className="text-gray-300 text-center leading-relaxed">
                  From kitchen to doorstep — every meal is prepared with <span className="font-semibold text-orange-500">premium ingredients</span> and <span className="font-semibold text-orange-500">strict hygiene standards</span>.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-sm text-gray-300 bg-orange-500/20 inline-block px-8 py-4 rounded-full font-semibold shadow-md border border-orange-500">
              All prices include HST. No hidden fees.
            </p>
          </div>
        </div>
      </section>

      {/* Fresh Meal Prep Showcase Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">Fresh, Delicious & <span className="text-orange-500">Protein-Packed</span></h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">Every meal is prepared fresh daily in our certified kitchen with premium ingredients</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Blurred Image 1 - Burrito Bowl */}
            <div className="relative overflow-hidden rounded-2xl shadow-2xl hover:shadow-orange-500/30 transition-all duration-300 hover:scale-105 h-64 md:h-96">
              <img
                src="https://images.unsplash.com/photo-1668665771757-4d42737d295a"
                alt="Perfectly Portioned Burrito Bowl"
                className="w-full h-full object-cover blur-[2px] hover:blur-none transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-6">
                <div>
                  <h3 className="text-3xl font-bold text-white mb-2">Perfectly Portioned</h3>
                  <p className="text-gray-200 text-lg">Balanced macros in every container</p>
                </div>
              </div>
            </div>

            {/* Blurred Image 2 - Salad Bowl */}
            <div className="relative overflow-hidden rounded-2xl shadow-2xl hover:shadow-orange-500/30 transition-all duration-300 hover:scale-105 h-64 md:h-96">
              <img
                src="https://images.unsplash.com/photo-1546069901-ba9599a7e63c"
                alt="Fresh Premium Ingredients Salad Bowl"
                className="w-full h-full object-cover blur-[2px] hover:blur-none transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-6">
                <div>
                  <h3 className="text-3xl font-bold text-white mb-2">Premium Ingredients</h3>
                  <p className="text-gray-200 text-lg">Only the freshest, highest quality ingredients</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-16">
            <div className="text-center p-6 bg-orange-500 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="text-4xl font-bold text-white mb-2">4500+</div>
              <div className="text-white font-semibold">Customers Served</div>
            </div>
            <div className="text-center p-6 bg-orange-500 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="text-4xl font-bold text-white mb-2">100,000+</div>
              <div className="text-white font-semibold">Meals Delivered</div>
            </div>
            <div className="text-center p-6 bg-orange-500 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="text-4xl font-bold text-white mb-2">30g+</div>
              <div className="text-white font-semibold">Protein Per Meal</div>
            </div>
          </div>
        </div>
      </section>

      {/* Cities We Deliver Section */}
      <section id="coverage" className="py-12 md:py-20 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 z-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white">
              Cities We <span className="text-orange-500">Deliver To</span>
            </h2>
            <div className="flex items-center justify-center gap-2 mb-6">
              <MapPin className="w-8 h-8 text-orange-500 animate-bounce" />
              <p className="text-xl md:text-2xl text-gray-300 font-semibold">Serving the Greater Toronto Area</p>
            </div>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Fresh, high-protein meals delivered Monday to Friday across all major GTA regions
            </p>
          </div>

          {/* Modern City Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 max-w-7xl mx-auto">
            {/* Toronto (GTA) */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-orange-500 hover:border-orange-400 hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-300 hover:scale-105 hover:-translate-y-2 group">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-orange-500 rounded-lg group-hover:bg-orange-600 transition-colors">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                </div>
                <CardTitle className="text-xl font-bold text-white group-hover:text-orange-500 transition-colors">
                  Toronto (GTA)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Downtown
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    North York
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Etobicoke
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    York
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Scarborough
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* York Region */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-orange-500 hover:border-orange-400 hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-300 hover:scale-105 hover:-translate-y-2 group">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-orange-500 rounded-lg group-hover:bg-orange-600 transition-colors">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                </div>
                <CardTitle className="text-xl font-bold text-white group-hover:text-orange-500 transition-colors">
                  York Region
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Vaughan
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Richmond Hill
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Markham
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Durham */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-orange-500 hover:border-orange-400 hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-300 hover:scale-105 hover:-translate-y-2 group">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-orange-500 rounded-lg group-hover:bg-orange-600 transition-colors">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                </div>
                <CardTitle className="text-xl font-bold text-white group-hover:text-orange-500 transition-colors">
                  Durham
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Pickering
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Ajax
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Peel & Halton */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-orange-500 hover:border-orange-400 hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-300 hover:scale-105 hover:-translate-y-2 group">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-orange-500 rounded-lg group-hover:bg-orange-600 transition-colors">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                </div>
                <CardTitle className="text-xl font-bold text-white group-hover:text-orange-500 transition-colors">
                  Peel & Halton
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Mississauga
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Brampton
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Oakville
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Milton
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Burlington
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Caledon
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Waterloo Region */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-orange-500 hover:border-orange-400 hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-300 hover:scale-105 hover:-translate-y-2 group">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-orange-500 rounded-lg group-hover:bg-orange-600 transition-colors">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                </div>
                <CardTitle className="text-xl font-bold text-white group-hover:text-orange-500 transition-colors">
                  Waterloo Region
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Kitchener
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Waterloo
                  </li>
                  <li className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Cambridge
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <p className="text-xl text-gray-300 mb-6">
              Don't see your area? <span className="text-orange-500 font-semibold">Contact us</span> — we're expanding!
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-12 md:py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white">Frequently Asked <span className="text-orange-500">Questions</span></h2>
            <p className="text-xl text-gray-300">Everything you need to know about Just Protein</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* FAQ 1 - Orange Theme */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 border-2 border-orange-400">
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                When are meals delivered?
              </h3>
              <p className="text-orange-50">
                We deliver fresh meals right to your door <strong className="text-white">Monday to Friday — before 8 AM!</strong>
              </p>
            </div>

            {/* FAQ 2 - Orange Theme */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 border-2 border-orange-400">
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Do you have a fixed menu?
              </h3>
              <p className="text-orange-50">
                No fixed menu! In a world full of surprises — let your meals be part of it. Enjoy new flavors every week curated by our chefs.
              </p>
            </div>

            {/* FAQ 3 - Orange Theme */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 border-2 border-orange-400">
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                How do I choose my meals?
              </h3>
              <p className="text-orange-50">
                If you're on the 2 Meals-a-Day Plan, you can select which meals you'd like — Breakfast, Lunch, or Dinner.
              </p>
            </div>

            {/* FAQ 4 - Orange Theme */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 border-2 border-orange-400">
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
                Can I customize my meals?
              </h3>
              <p className="text-orange-50">
                Absolutely! Inform us about your preferences, avoidances, or any allergies — we'll take care of the rest.
              </p>
            </div>

            {/* FAQ 5 - Orange Theme */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 border-2 border-orange-400">
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                How does the Veg/Non-Veg plan work?
              </h3>
              <p className="text-orange-50">
                For the 2-Day Non-Veg / 3-Day Veg Plan, non-veg meals are sent by default on Wednesday and Friday, unless you specify otherwise.
              </p>
            </div>

            {/* FAQ 6 - Orange Theme */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 border-2 border-orange-400">
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Do you offer weekend meals?
              </h3>
              <p className="text-orange-50">
                Yes! We can send two meal bags on Friday to cover your Saturday meals — for an additional fee.
              </p>
            </div>

            {/* FAQ 7 - Orange Theme */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 border-2 border-orange-400">
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                How do I make a payment?
              </h3>
              <p className="text-orange-50 flex items-center gap-2">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send your e-transfer to <strong className="text-white ml-1">justproteinca@gmail.com</strong>
              </p>
            </div>

            {/* FAQ 8 - Orange Theme */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 border-2 border-orange-400">
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Need a break?
              </h3>
              <p className="text-orange-50">
                You can pause your plan anytime and resume whenever you're ready — with zero hassle.
              </p>
            </div>

            {/* FAQ 9 - Orange Theme (Full Width) */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 border-2 border-orange-400 md:col-span-2">
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Moved or changing address?
              </h3>
              <p className="text-orange-50">
                Simply submit a request from your dashboard, and we'll make sure your meals reach your new doorstep on time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-16 border-t-4 border-orange-500">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 mb-12">
            {/* Brand Section */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                <img
                  src="/images/just-protein-logo.png"
                  alt="Just Protein Logo"
                  className="h-20 w-20 object-contain"
                />
                <div className="flex flex-col items-start">
                  <span className="text-2xl font-bold text-white">Just Protein</span>
                  <span className="text-sm text-orange-500 font-semibold">Gym Meals Service Provider</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Fresh, high-protein meals delivered to your door. Fuel your fitness journey with premium meal prep across the GTA.
              </p>
            </div>

            {/* Quick Links */}
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold text-orange-500 mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#why" className="text-gray-400 hover:text-orange-500 transition-colors">Why Just Protein?</a></li>
                <li><a href="#coverage" className="text-gray-400 hover:text-orange-500 transition-colors">Delivery Areas</a></li>
                <li><a href="#faq" className="text-gray-400 hover:text-orange-500 transition-colors">FAQ</a></li>
                <li><button onClick={() => navigate('/signup/meal-plans')} className="text-gray-400 hover:text-orange-500 transition-colors">Sign Up Today</button></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold text-orange-500 mb-4">Get In Touch</h3>
              <div className="space-y-3 text-sm">
                <a href="tel:2265046197" className="flex items-center justify-center md:justify-start gap-2 text-gray-400 hover:text-orange-500 transition-colors">
                  <Phone className="w-5 h-5" />
                  226-504-6197
                </a>
                <a href="https://instagram.com/justproteincanada" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center md:justify-start gap-2 text-gray-400 hover:text-orange-500 transition-colors">
                  <Instagram className="w-5 h-5" />
                  @justproteincanada
                </a>
                <div className="flex items-center justify-center md:justify-start gap-2 text-gray-400">
                  <MapPin className="w-5 h-5" />
                  Serving Greater Toronto Area
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-sm text-gray-400 flex items-center justify-center gap-2">
              © 2025 Just Protein. All rights reserved.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Fresh meals delivered Mon–Fri, 1:00 AM – 8:00 AM
            </p>
          </div>
        </div>
      </footer>

      {/* Weekly Menu Modal */}
      {showMenuModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border-2 border-orange-500 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-orange-500/30">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <Calendar className="w-6 h-6 text-orange-500" />
                This Week's Menu
              </h2>
              <button
                onClick={() => setShowMenuModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {menuLoading ? (
                <div className="text-center py-12 text-gray-400">Loading menu...</div>
              ) : weeklyMenu.length === 0 ? (
                <div className="text-center py-12 text-gray-400">No menu available yet.</div>
              ) : (
                <>
                  {/* Day Tabs */}
                  <div className="flex gap-2 overflow-x-auto pb-4 mb-4 no-scrollbar">
                    {weeklyMenu.map((day) => {
                      const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });
                      const isSelected = selectedDay?.day === day.day;
                      const isToday = day.day === todayName;
                      return (
                        <button
                          key={day.day}
                          onClick={() => setSelectedDay(day)}
                          className={`flex-shrink-0 px-4 py-2 rounded-lg border-2 transition-all ${
                            isSelected
                              ? 'bg-orange-500 border-orange-600 text-white shadow-md'
                              : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-orange-400'
                          }`}
                        >
                          <div className="text-xs font-bold uppercase">{day.day.slice(0, 3)}</div>
                          {isToday && (
                            <div className="text-[10px] mt-1 bg-white text-orange-600 rounded px-1 font-bold">TODAY</div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Selected Day Meals */}
                  {selectedDay && (
                    <div className="space-y-3">
                      {[
                        { key: 'breakfast', label: 'Breakfast', color: 'text-amber-500' },
                        { key: 'lunch', label: 'Lunch', color: 'text-orange-500' },
                        { key: 'dinner', label: 'Dinner', color: 'text-indigo-400' },
                        { key: 'nonveg_item', label: 'Non-Veg Option', color: 'text-red-400' },
                        { key: 'nonveg_item_2', label: 'Non-Veg Option 2', color: 'text-red-400' },
                      ].map(({ key, label, color }) => {
                        const meal = selectedDay[key];
                        if (!meal?.name) return null;
                        return (
                          <div key={key} className="flex gap-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                            <div>
                              <span className={`text-xs font-bold ${color} uppercase tracking-wide`}>{label}</span>
                              <h4 className="font-bold text-white">{meal.name}</h4>
                              {(meal.protein > 0 || meal.calories > 0) && (
                                <p className="text-xs text-gray-400 mt-1">
                                  {meal.protein > 0 && `${meal.protein}g Pro`}
                                  {meal.protein > 0 && meal.calories > 0 && ' • '}
                                  {meal.calories > 0 && `${meal.calories} Cal`}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {!selectedDay.breakfast?.name && !selectedDay.lunch?.name && !selectedDay.dinner?.name && (
                        <div className="text-center py-8 text-gray-500 italic">
                          No menu set for {selectedDay.day} yet.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-orange-500/30 text-center">
              <Button
                className="bg-orange-500 text-white hover:bg-orange-600 font-semibold px-8"
                onClick={() => { setShowMenuModal(false); navigate('/signup/meal-plans'); }}
              >
                Sign Up & Get Started
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Price Calculator Modal */}
      <PriceCalculatorModal
        isOpen={isCalculatorOpen}
        onClose={handleCloseCalculator}
        onNavigate={onNavigate}
      />
      <PlanPricingModal
        isOpen={isPricingOpen}
        onClose={() => setIsPricingOpen(false)}
      />
    </div>
  );
};

export default LandingPage;
